"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { Camera, ImagePlus, Mic, Smile, User, X } from "lucide-react";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Button } from "ui";
import { RichTextEditor, useRichEditor } from "editor";

import { useMember } from "@/lib/state/member";
import { useStudio } from "@/lib/state/studio";
import { useMentionSuggestion } from "@/hooks/use-mention-suggestion";
import { uploadImage, validateImage } from "@/lib/image-upload";
import VoiceMessageRecorder from "@/components/voice-message-recorder";
import VideoMessageRecorder from "@/components/video-message-recorder";

interface EmojiSelectPayload {
  native: string;
}

export interface CommentComposerHandle {
  focus: () => void;
}

interface CommentComposerProps {
  onSubmit: (
    content: string,
    imageFile: File | null | undefined,
  ) => Promise<void>;
  onAudioSubmit?: (
    audioUrl: string,
    audioDuration: number,
    audioMimeType: string,
  ) => Promise<void>;
  onVideoSubmit?: (videoId: string, videoDuration: number) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  hideAvatar?: boolean;
}

/**
 * Variant of the MessageComposer used for post comments. Uses the
 * `comment` editor preset which disables all formatting marks (bold,
 * italic, lists, etc.) and exposes only the minimum toolbar: image
 * attach + emoji + Post button.
 */
const CommentComposer = forwardRef<CommentComposerHandle, CommentComposerProps>(
  function CommentComposer(
    {
      onSubmit,
      onAudioSubmit,
      onVideoSubmit,
      placeholder = "What are your thoughts?",
      disabled = false,
      hideAvatar = false,
    },
    ref,
  ) {
    const { currentMember } = useMember();
    const { studio } = useStudio();
    const mentionSuggestion = useMentionSuggestion();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const emojiPickerRef = useRef<HTMLDivElement>(null);

    const [isVideoMode, setIsVideoMode] = useState(false);
    const [isAudioMode, setIsAudioMode] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const editor = useRichEditor({
      preset: "comment",
      placeholder,
      editable: !disabled && !isSubmitting,
      onUpdate: (ed) => setIsEmpty(ed.isEmpty),
      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false),
      mentionSuggestion,
    });

    useEffect(() => {
      if (editor) setIsEmpty(editor.isEmpty);
    }, [editor]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus("end"),
      }),
      [editor],
    );

    const handleSubmit = useCallback(async () => {
      if (!editor) return;
      const text = editor.getText().trim();
      if (!text && !imageFile) return;

      // Capture content first, then clear immediately for instant feedback
      const html = editor.getHTML();
      const uploadedImage = imageFile ?? null;
      editor.commands.clearContent();
      setImageFile(null);
      setImagePreview(null);

      setIsSubmitting(true);
      try {
        await onSubmit(html, uploadedImage);
      } finally {
        setIsSubmitting(false);
      }
    }, [editor, imageFile, onSubmit]);

    const handleEmojiSelect = useCallback(
      (emoji: EmojiSelectPayload) => {
        if (!editor) return;
        editor.chain().focus().insertContent(emoji.native).run();
        setShowEmojiPicker(false);
      },
      [editor],
    );

    const handleFileSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validation = validateImage(file);
        if (!validation?.valid) return;
        setImageFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target?.result as string);
        reader.readAsDataURL(file);
        e.target.value = "";
      },
      [],
    );

    const removeImage = useCallback(() => {
      setImageFile(null);
      setImagePreview(null);
    }, []);

    // Close emoji picker on outside click
    useEffect(() => {
      if (!showEmojiPicker) return;
      const onClickOutside = (e: MouseEvent) => {
        if (
          emojiPickerRef.current &&
          !emojiPickerRef.current.contains(e.target as Node)
        ) {
          setShowEmojiPicker(false);
        }
      };
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }, [showEmojiPicker]);

    // Enter submits; Shift+Enter inserts a newline (default); Cmd/Ctrl+Enter also submits
    useEffect(() => {
      if (!editor) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      };
      const el = editor.view.dom;
      el.addEventListener("keydown", handler);
      return () => el.removeEventListener("keydown", handler);
    }, [editor, handleSubmit]);

    const profileImage = currentMember?.profile?.image ?? null;
    const displayName = currentMember?.profile?.displayName ?? "User";

    const canSubmit = !disabled && !isSubmitting && (!isEmpty || !!imageFile);

    if (isVideoMode) {
      return (
        <VideoMessageRecorder
          studioId={studio?.id ?? ""}
          onSend={async (videoId, videoDuration) => {
            await onVideoSubmit?.(videoId, videoDuration);
            setIsVideoMode(false);
          }}
          onCancel={() => setIsVideoMode(false)}
        />
      );
    }

    if (isAudioMode) {
      return (
        <VoiceMessageRecorder
          studioId={studio?.id ?? ""}
          onSend={async (audioUrl, audioDuration, audioMimeType) => {
            await onAudioSubmit?.(audioUrl, audioDuration, audioMimeType);
            setIsAudioMode(false);
          }}
          onCancel={() => setIsAudioMode(false)}
        />
      );
    }

    return (
      <div className="flex gap-2.5">
        {/* Avatar */}
        {!hideAvatar && (
          <div className="shrink-0 pt-1">
            {profileImage ? (
              <Image
                src={profileImage}
                width={28}
                height={28}
                alt={displayName}
                className="h-7 w-7 shrink-0 rounded-md object-cover"
              />
            ) : (
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Card */}
        <div className="relative min-w-0 flex-1">
          <RichTextEditor
            editor={editor}
            isFocused={isFocused}
            className="text-sm"
            minHeight={32}
            footer={() => (
              <div className="flex items-center justify-between px-2 py-1.5">
                <div className="flex items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    icon={<ImagePlus />}
                    title="Add image"
                    aria-label="Add image"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowEmojiPicker((p) => !p)}
                    icon={<Smile />}
                    title="Emoji"
                    aria-label="Open emoji picker"
                  />
                  {onAudioSubmit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsAudioMode(true)}
                      icon={<Mic />}
                      title="Voice message"
                      aria-label="Record voice message"
                    />
                  )}
                  {onVideoSubmit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsVideoMode(true)}
                      icon={<Camera />}
                      title="Video message"
                      aria-label="Record video message"
                    />
                  )}
                </div>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  size="sm"
                  className="min-w-[88px]"
                >
                  {isSubmitting ? (
                    "Posting"
                  ) : (
                    <>
                      Post{" "}
                      <kbd className="ml-1.5 text-[10px] font-normal opacity-60">
                        ↵
                      </kbd>
                    </>
                  )}
                </Button>
              </div>
            )}
          />

          {imagePreview && (
            <div className="relative mt-2 inline-block">
              <Image
                src={imagePreview}
                alt="Attachment preview"
                width={160}
                height={120}
                className="max-h-32 rounded-lg object-cover"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-foreground p-0.5 text-background shadow-sm hover:bg-foreground/80"
                aria-label="Remove image"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {showEmojiPicker && (
            <div
              ref={emojiPickerRef}
              className="absolute bottom-full left-0 z-50 mb-2"
            >
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme="light"
                previewPosition="none"
                skinTonePosition="search"
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default CommentComposer;
